import styled from 'styled-components';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import {
	MAX_ADDRESS_LINES,
	PALETTE,
	STAMP_DESIGN,
} from '@dealstack/shared/lib/common/business/stampDesign';
import { InvestorCursive } from './scribbles/investorCursive.tsx';
import { WitnessCursive } from './scribbles/witnessCursive.tsx';
import type { StampPreviewProps } from './StampPreview.types.ts';

// Colours come from the shared stamp palette so the preview can't drift from
// the rendered PDF (local aliases keep the styled-components readable).
const {
	brand: BRAND,
	lightGrey: DIVIDER,
	textDark: TEXT_DARK,
	textMuted: TEXT_MUTED,
	white: WHITE,
} = PALETTE;

const HEADER_TEXT = 'Verified by Dealstack';

// A date box has no scribble — it's stamped with the real date at sign time.
const DATE_PLACEHOLDER_TEXT = '01 January 2026';

// Point size the renderer gives the date (STAMP_DESIGN.signature.maxHeight × 0.7), so the
// preview shows it at the size it lands at.
const DATE_FONT_PT = STAMP_DESIGN.signature.maxHeight * 0.7;

// Placeholder ID echoing the real stamp's UUIDv7 footer, truncated.
const PREVIEW_ID = '019ebba8-3a4c-7f50-9d2e-…-3de';

// A witness stamp reserves MAX_ADDRESS_LINES for the wrapped address plus a row for the
// occupation — that reservation is what makes its box taller than a party box, so the preview
// has to show the rows or the extra height reads as arbitrary. Placeholder text only; the real
// address is captured from the witness at signing time.
const PREVIEW_ADDRESS_LINES = ['12 High Street,', 'Kensington,', 'London,', 'SW1A 1AA'].slice(
	0,
	MAX_ADDRESS_LINES
);
const PREVIEW_OCCUPATION = 'occupation';

// Scribble height (PDF pt) driven by $scale (same scalar as the text) so the
// stamp scales in lockstep, unlike a height-% scribble. From the renderer's token.
const SCRIBBLE_HEIGHT_PT = STAMP_DESIGN.signature.maxHeight;
// Compact stamps (no metadata, 210×70) give the scribble the whole interior so
// it reads larger; still * $scale to stay locked to the header.
const SCRIBBLE_HEIGHT_COMPACT_PT = 50;

const Root = styled.div`
	position: relative;
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	border: 2px solid ${BRAND};
	border-radius: 6px;
	background: ${WHITE};
	overflow: visible;
	pointer-events: none;
	display: flex;
	flex-direction: column;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`;

const Header = styled.span<{ $scale: number }>`
	position: absolute;
	top: ${({ $scale }) => -7 * $scale}px;
	left: ${({ $scale }) => 14 * $scale}px;
	background: ${WHITE};
	padding: 0 ${({ $scale }) => 6 * $scale}px;
	font-size: ${({ $scale }) => Math.max(8, 11 * $scale)}px;
	font-weight: 600;
	color: ${BRAND};
	line-height: 1;
	white-space: nowrap;
	pointer-events: none;
`;

// Full mode: ~33% band, scribble sized off $scale and bottom-anchored so it
// rests on the divider like a signing line. Compact mode centres a larger one.
const ScribbleArea = styled.div<{ $scale: number; $compact: boolean }>`
	flex: ${({ $compact }) => ($compact ? '1 1 auto' : '0 0 33%')};
	display: flex;
	align-items: ${({ $compact }) => ($compact ? 'center' : 'flex-end')};
	justify-content: center;
	padding: ${({ $scale, $compact }) => ($compact ? 4 : 6) * $scale}px
		${({ $scale }) => 18 * $scale}px ${({ $scale, $compact }) => ($compact ? 4 : 0) * $scale}px
		${({ $scale }) => 18 * $scale}px;
	overflow: hidden;

	& > svg {
		width: auto;
		height: ${({ $scale, $compact }) =>
			($compact ? SCRIBBLE_HEIGHT_COMPACT_PT : SCRIBBLE_HEIGHT_PT) * $scale}px;
		max-width: 100%;
		max-height: 100%;
	}
`;

const Divider = styled.div<{ $scale: number }>`
	margin: 0 ${({ $scale }) => 18 * $scale}px;
	border-top: 1px solid ${DIVIDER};
`;

// Metadata block: tight top padding + small line gap mirror the renderer's
// reduced meta.lineGap so the Name row sits close to the divider.
const MetaArea = styled.div<{ $scale: number }>`
	flex: 1 1 auto;
	padding: ${({ $scale }) => 3 * $scale}px ${({ $scale }) => 18 * $scale}px
		${({ $scale }) => 8 * $scale}px;
	display: flex;
	flex-direction: column;
	gap: ${({ $scale }) => 1 * $scale}px;
	overflow: hidden;
`;

const MetaLine = styled.div<{ $scale: number }>`
	font-size: ${({ $scale }) => 9 * $scale}px;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const Label = styled.span`
	color: ${TEXT_MUTED};
`;

const NameValue = styled.span`
	color: ${TEXT_DARK};
	font-weight: 600;
`;

const Value = styled.span`
	color: ${TEXT_DARK};
`;

const DetailValue = styled.span`
	color: ${TEXT_MUTED};
`;

// Date boxes stamp the date and nothing else, so the preview is the date alone. The dashed
// outline is an editor affordance only — without it an unselected box would be invisible.
const DateRoot = styled.div`
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	border: 1px dashed ${BRAND};
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	pointer-events: none;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`;

const DateText = styled.span<{ $scale: number }>`
	font-size: ${({ $scale }) => Math.max(8, DATE_FONT_PT * $scale)}px;
	color: ${TEXT_DARK};
	white-space: nowrap;
`;

const Footer = styled.span<{ $scale: number }>`
	position: absolute;
	bottom: ${({ $scale }) => -4 * $scale}px;
	left: 50%;
	transform: translateX(-50%);
	background: ${WHITE};
	padding: 0 ${({ $scale }) => 6 * $scale}px;
	font-size: ${({ $scale }) => Math.max(6, 7 * $scale)}px;
	color: ${TEXT_MUTED};
	line-height: 1;
	white-space: nowrap;
	pointer-events: none;
`;

// Placeholder relationship line (witness of / represented by / on behalf of);
// null for plain signers. Real stamp resolves the parent name at runtime.
const buildDetailLine = (
	isWitness: boolean,
	roleType: DocumentSigningRoleType | undefined
): { label: string; value: string } | null => {
	if (isWitness) {
		return { label: 'Witness of ', value: 'investor' };
	}
	if (roleType === DocumentSigningRoleType.REPRESENTATIVE) {
		return { label: 'Represented by ', value: 'investor' };
	}
	if (roleType === DocumentSigningRoleType.PIV) {
		return { label: 'on behalf of ', value: 'entity' };
	}
	return null;
};

const pickScribble = (isWitness: boolean) => {
	return isWitness ? <WitnessCursive /> : <InvestorCursive />;
};

const formatToday = (): string =>
	new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export const StampPreview = ({
	slotLabel,
	isWitness,
	roleType,
	kind,
	showMetadata,
	scale,
}: StampPreviewProps) => {
	// Hide metadata when the template opts out or the box is too small to read;
	// compact mode is shorter (≈210×70) so the scribble fills the interior.
	const showDetail = scale >= 0.5 && showMetadata;
	const compact = !showMetadata;
	const detail = buildDetailLine(isWitness, roleType);

	if (kind === 'date') {
		return (
			<DateRoot data-testid="stamp-preview">
				<DateText $scale={scale}>{DATE_PLACEHOLDER_TEXT}</DateText>
			</DateRoot>
		);
	}

	return (
		<Root data-testid="stamp-preview">
			<Header $scale={scale}>{HEADER_TEXT}</Header>
			<ScribbleArea $scale={scale} $compact={compact}>
				{pickScribble(isWitness)}
			</ScribbleArea>
			{showDetail ? (
				<>
					<Divider $scale={scale} />
					<MetaArea $scale={scale} data-testid="stamp-preview-meta">
						<MetaLine $scale={scale}>
							<Label>Name: </Label>
							<NameValue>{slotLabel}</NameValue>
						</MetaLine>
						{detail ? (
							<MetaLine $scale={scale}>
								<Label>{detail.label}</Label>
								<DetailValue>{detail.value}</DetailValue>
							</MetaLine>
						) : null}
						{isWitness ? (
							<>
								{PREVIEW_ADDRESS_LINES.map((line, index) => (
									<MetaLine $scale={scale} key={line}>
										{index === 0 ? <Label>Address: </Label> : null}
										<Value>{line}</Value>
									</MetaLine>
								))}
								<MetaLine $scale={scale}>
									<Label>Occupation: </Label>
									<Value>{PREVIEW_OCCUPATION}</Value>
								</MetaLine>
							</>
						) : null}
						<MetaLine $scale={scale}>
							<Label>Date: </Label>
							<Value>{formatToday()}</Value>
						</MetaLine>
					</MetaArea>
					<Footer $scale={scale}>ID: {PREVIEW_ID}</Footer>
				</>
			) : null}
		</Root>
	);
};
